import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { NumberField } from "@/components/generic/NumberField";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { createRangeFilter, rangeFilterToForm } from "@/utils/filter-utils";
import { isOneOf } from "@/utils/type-guards";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  number: z
    .object({
      min: z.number().nullable(),
      max: z.number().nullable(),
    })
    .refine(
      (data) => {
        if (data.min === null || data.max === null) return true;
        return data.min <= data.max;
      },
      {
        message: '"Flere end" kan ikke være mindre end "færre end"',
        path: ["min"],
      },
    ),
});

export function NumberFilter({
  group: { title, description, type = "number" },
  formId,
  queryFilters,
  update,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  {
    type: "number" | "percentage";
  }
>) {
  let numberFilter = queryFilters.value;
  if (
    isOneOf(numberFilter?.operator, "EventFilter", "DepartmentSize") ||
    !numberFilter?.values
  )
    numberFilter = undefined;

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      number: rangeFilterToForm(numberFilter),
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const submit = handleSubmit((data) => {
    const numberFilter = createRangeFilter(data.number);

    update({ value: numberFilter });
  });

  return (
    <Form
      className="flex size-full flex-col items-center justify-between"
      id={formId}
      onSubmit={submit}
    >
      <div className="flex flex-col items-center gap-y-6">
        <SegmentHeader
          title={title}
          description={description}
          className="mb-9"
        />
        <div className="flex gap-x-4">
          <div className="flex items-center gap-x-4">
            <NumberField
              name="number.min"
              aria-label="Flere end"
              placeholder="Flere end"
              control={control}
              minValue={0}
            />
            {type === "percentage" && (
              <span className="text-base font-medium">%</span>
            )}
          </div>
          <div className="flex items-center gap-x-4">
            <NumberField
              aria-label="Færre end"
              placeholder="Færre end"
              name="number.max"
              control={control}
              minValue={0}
            />
            {type === "percentage" && (
              <span className="text-base font-medium">%</span>
            )}
          </div>
        </div>
      </div>
    </Form>
  );
}
